import { computed, readonly, ref, shallowRef, watchEffect } from "vue";
import { deleteMemberAddresses, getMyAddresses, updateMemberAddresses } from "@/core/api/graphql/account";
import { SortDirection } from "@/core/enums";
import { getSortingExpression, isEqualAddresses, Logger, toInputAddress } from "@/core/utilities";
import { useUser } from "./useUser";
import type { InputMemberAddressType, MemberAddressTypeExtension } from "@/core/api/graphql/types";
import type { AnyAddressType, ISortInfo } from "@/core/types";

const loading = ref(false);
const defaultShippingAddressCountryCode = ref<string>("");
const defaultBillingAddressCountryCode = ref<string>("");
const addresses = shallowRef<MemberAddressTypeExtension[]>([]);

const sort = ref<ISortInfo>({
  column: "createdDate",
  direction: SortDirection.Ascending,
});

watchEffect(() => {
  defaultShippingAddressCountryCode.value =
    addresses.value.find((address) => address.isDefaultShippingAddressTL)?.countryCode ?? "";
});

watchEffect(() => {
  defaultBillingAddressCountryCode.value =
    addresses.value.find((address) => address.isDefaultBillingAddressTL)?.countryCode ?? "";
});

export function useUserAddresses() {
  const { user } = useUser();

  function isExistAddress(address: AnyAddressType): boolean {
    return addresses.value.some((item) => isEqualAddresses(item, address));
  }

  async function setDefaultBillingAddress(address: MemberAddressTypeExtension): Promise<void> {
    addresses.value.find((item) => item.id === address.id)!.isDefaultBillingAddressTL = true;
    resetDefaultBillingAddress(address);

    await updateAddresses(addresses.value);
  }

  async function setDefaultShippingAddress(address: MemberAddressTypeExtension): Promise<void> {
    addresses.value.find((item) => item.id === address.id)!.isDefaultShippingAddressTL = true;
    resetDefaultShippingAddress(address);

    await updateAddresses(addresses.value);
  }

  function resetDefaultShippingAddress(address: MemberAddressTypeExtension): void {
    addresses.value.forEach((item) => {
      if (item.id !== address.id) {
        item.isDefaultShippingAddressTL = false;
      }
    });
  }

  function resetDefaultBillingAddress(address: MemberAddressTypeExtension): void {
    addresses.value.forEach((item) => {
      if (item.id !== address.id) {
        item.isDefaultBillingAddressTL = false;
      }
    });
  }

  async function fetchAddresses() {
    loading.value = true;

    const sortingExpression = getSortingExpression(sort.value);

    try {
      addresses.value = await getMyAddresses({ sort: sortingExpression });
    } catch (e) {
      Logger.error(`${useUserAddresses.name}.${fetchAddresses.name}`, e);
      throw e;
    } finally {
      loading.value = false;
    }
  }

  async function updateAddresses(items: MemberAddressTypeExtension[]): Promise<void> {
    loading.value = true;

    const inputAddresses: InputMemberAddressType[] = items.map(toInputAddress);

    try {
      const result = await updateMemberAddresses(user.value.memberId!, inputAddresses, {
        sort: getSortingExpression(sort.value),
        first: 1000,
      });

      // Validate proper response to ensure update was successful
      if (!result?.id) {
        throw new Error("Address update failed: missing update ID.");
      }

      // Assign fresh addresses
      addresses.value = result.addresses?.items ?? [];
    } catch (e) {
      Logger.error(`${useUserAddresses.name}.${updateAddresses.name}`, e);
      throw e;
    } finally {
      loading.value = false;
    }
  }

  async function addOrUpdateAddresses(items: MemberAddressTypeExtension[]): Promise<void> {
    if (!items.length) {
      return;
    }

    loading.value = true;

    const updatedAddresses: MemberAddressTypeExtension[] = addresses.value.slice();

    items.forEach((newAddress: MemberAddressTypeExtension) => {
      const index = updatedAddresses.findIndex((oldAddress) => oldAddress.id === newAddress.id);

      if (index === -1) {
        updatedAddresses.push(newAddress);
      } else {
        updatedAddresses.splice(index, 1, newAddress);
      }

      if (newAddress.isDefaultBillingAddressTL) {
        updatedAddresses.forEach((item) => {
          if (item.id !== newAddress.id) {
            item.isDefaultBillingAddressTL = false;
          }
        });
      }

      if (newAddress.isDefaultShippingAddressTL) {
        updatedAddresses.forEach((item) => {
          if (item.id !== newAddress.id) {
            item.isDefaultShippingAddressTL = false;
          }
        });
      }
    });

    await updateAddresses(updatedAddresses);
  }

  async function removeAddresses(items: MemberAddressTypeExtension[]): Promise<void> {
    if (!items.length) {
      return;
    }

    loading.value = true;

    const inputAddresses: InputMemberAddressType[] = items.map(toInputAddress);

    try {
      const result = await deleteMemberAddresses(inputAddresses, user.value.memberId!);

      // Validate proper response to ensure deletion was successful
      if (!result?.id) {
        throw new Error("Address deletion failed: missing update ID.");
      }

      // Assign fresh addresses
      addresses.value = result.addresses?.items ?? [];
    } catch (e) {
      Logger.error(`${useUserAddresses.name}.${removeAddresses.name}`, e);
      throw e;
    } finally {
      loading.value = false;
    }
  }

  return {
    sort,
    isExistAddress,
    fetchAddresses,
    addOrUpdateAddresses,
    removeAddresses,
    setDefaultBillingAddress,
    setDefaultShippingAddress,
    loading: readonly(loading),
    addresses: computed(() => addresses.value),
    defaultShippingAddressCountryCode,
    defaultBillingAddressCountryCode,
  };
}
